import { useState } from 'react';
import { Mail, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Don't show banner if email is verified or user dismissed it
  if (!user || user.email_confirmed_at || dismissed) {
    return null;
  }

  const handleResendEmail = async () => {
    if (!user?.email) return;

    setResending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) throw error;

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-orange-50 border-b border-orange-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-shrink-0">
              <Mail className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-900">
                Please verify your email address to use generation features
              </p>
              <p className="text-xs text-orange-700 mt-0.5">
                Check your inbox at <strong>{user.email}</strong> for the confirmation link
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {resendSuccess ? (
              <div className="flex items-center gap-1.5 text-green-700 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Email sent!
              </div>
            ) : resendError ? (
              <div className="flex items-center gap-1.5 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {resendError}
              </div>
            ) : (
              <button
                onClick={handleResendEmail}
                disabled={resending}
                className="text-sm font-medium text-orange-700 hover:text-orange-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? 'Sending...' : 'Resend Email'}
              </button>
            )}

            <button
              onClick={() => setDismissed(true)}
              className="text-orange-600 hover:text-orange-800 ml-2"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
